// RadialView — concentric-rings sunburst.
//
// Root sits in the center; each subsequent depth level is rendered as
// a ring of arc segments. An arc's angular extent is proportional to
// the leaf count of its subtree, so wide teams visually dominate.
//
// Click an arc → onSelectUser. Hover surfaces a tooltip with the
// person's name + title.
//
// Pure SVG + manual arc math, no d3. Good up to ~6 levels deep before
// outer rings get unreadable.
import React, { useMemo, useState } from "react";
import { childrenOf, colorFor, nameOf, nodeMatches, withSyntheticRoot } from "./shared";

const RING        = 56;
const CENTER_R    = 40;
const SVG_PADDING = 16;
const TAU         = Math.PI * 2;

// Recursive leaf count (used to weight angular extents). Synthetic
// root with no real subtree returns 1 so the centre still renders.
function leafCount(n) {
  const kids = childrenOf(n);
  if (kids.length === 0) return 1;
  return kids.reduce((acc, c) => acc + leafCount(c), 0);
}

// Walk the tree, assigning {a0, a1, depth} to every node so the render
// pass is a flat map.
function annotate(node, a0, a1, depth, out) {
  out.push({ node, a0, a1, depth });
  const kids = childrenOf(node);
  if (kids.length === 0) return;
  const totalLeaves = leafCount(node);
  let cursor = a0;
  kids.forEach((c) => {
    const span = ((a1 - a0) * leafCount(c)) / totalLeaves;
    annotate(c, cursor, cursor + span, depth + 1, out);
    cursor += span;
  });
}

// Wedge path between two radii and two angles. Angles are measured
// from -π/2 so the wedge starts at 12 o'clock and goes clockwise.
function wedgePath(cx, cy, r0, r1, a0, a1) {
  // SVG y grows downward, so we subtract π/2 to put 0 at the top.
  const sa0 = a0 - Math.PI / 2;
  const sa1 = a1 - Math.PI / 2;
  const x0o = cx + r1 * Math.cos(sa0);
  const y0o = cy + r1 * Math.sin(sa0);
  const x1o = cx + r1 * Math.cos(sa1);
  const y1o = cy + r1 * Math.sin(sa1);
  const x0i = cx + r0 * Math.cos(sa0);
  const y0i = cy + r0 * Math.sin(sa0);
  const x1i = cx + r0 * Math.cos(sa1);
  const y1i = cy + r0 * Math.sin(sa1);
  const largeArc = a1 - a0 > Math.PI ? 1 : 0;
  return [
    `M ${x0i} ${y0i}`,
    `L ${x0o} ${y0o}`,
    `A ${r1} ${r1} 0 ${largeArc} 1 ${x1o} ${y1o}`,
    `L ${x1i} ${y1i}`,
    `A ${r0} ${r0} 0 ${largeArc} 0 ${x0i} ${y0i}`,
    "Z",
  ].join(" ");
}

export default function RadialView({ tree, search, onSelectUser }) {
  const [hover, setHover] = useState(null);

  const { segments, maxDepth, matchSet } = useMemo(() => {
    const root = withSyntheticRoot(tree);
    const out = [];
    annotate(root, 0, TAU, 0, out);
    let md = 0;
    out.forEach((s) => { md = Math.max(md, s.depth); });

    // Build a set of node ids (or fallback identifiers) that match the
    // search so the render pass can dim non-matching wedges. Using id
    // where present, falling back to name as a coarse key.
    const matches = new Set();
    if (search) {
      const collect = (n) => {
        if (nodeMatches(n, search) && !n.__synthetic) {
          matches.add(n.id || nameOf(n));
        }
        childrenOf(n).forEach(collect);
      };
      collect(root);
    }
    return { segments: out, maxDepth: md, matchSet: matches };
  }, [tree, search]);

  const radius = CENTER_R + (maxDepth + 1) * RING;
  const size   = (radius + SVG_PADDING) * 2;
  const cx     = size / 2;
  const cy     = size / 2;

  return (
    <div className="p-4 overflow-auto max-h-[600px] flex justify-center">
      <div style={{ position: "relative" }}>
        <svg width={size} height={size} style={{ display: "block" }}>
          {/* Centre disc — root or synthetic "Organisation" */}
          {segments
            .filter((s) => s.depth === 0)
            .map((s, i) => {
              const color = colorFor(s.node.role);
              return (
                <g key={`c${i}`} style={{ cursor: s.node.__synthetic ? "default" : "pointer" }}
                   onClick={() => !s.node.__synthetic && onSelectUser?.(s.node)}>
                  <circle cx={cx} cy={cy} r={CENTER_R} fill={`${color}1f`} stroke={`${color}66`} />
                  <text x={cx} y={cy - 2} textAnchor="middle" fill="#fff" fontSize={11} fontWeight={600}>
                    {truncate(nameOf(s.node), 12)}
                  </text>
                  <text x={cx} y={cy + 12} textAnchor="middle" fill={color} fontSize={9}>
                    {s.node.__synthetic ? `${segments.length - 1} people` : (s.node.role || "")}
                  </text>
                </g>
              );
            })}

          {/* Rings — depth >= 1 */}
          {segments
            .filter((s) => s.depth > 0)
            .map((s, i) => {
              const r0 = CENTER_R + (s.depth - 1) * RING + 4;
              const r1 = CENTER_R + s.depth * RING;
              const color = colorFor(s.node.role);
              const isMatch = !search || matchSet.has(s.node.id || nameOf(s.node));
              return (
                <path
                  key={s.node.id || `s${i}`}
                  d={wedgePath(cx, cy, r0, r1, s.a0, s.a1)}
                  fill={`${color}33`}
                  stroke={`${color}aa`}
                  strokeWidth={0.6}
                  style={{ cursor: "pointer", opacity: isMatch ? 1 : 0.18 }}
                  onMouseEnter={() => setHover({ node: s.node, depth: s.depth })}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => onSelectUser?.(s.node)}
                />
              );
            })}
        </svg>

        {hover && (
          <div
            style={{
              position: "absolute", top: 8, left: 8,
              background: "#0a0a0a", border: "1px solid #1c1c1c",
              borderRadius: 8, padding: "8px 12px",
              fontSize: 11, color: "#fff", pointerEvents: "none",
              minWidth: 160, maxWidth: 220,
            }}
          >
            <div style={{ fontWeight: 600 }}>{nameOf(hover.node)}</div>
            <div style={{ color: "#888", fontSize: 10, marginTop: 2 }}>
              {hover.node.title || hover.node.designation || "—"}
            </div>
            <div style={{ color: colorFor(hover.node.role), fontSize: 10, marginTop: 2 }}>
              {hover.node.role || "staff"}
              {hover.node.department ? ` · ${hover.node.department}` : ""}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function truncate(s, n) {
  const str = String(s ?? "");
  return str.length > n ? str.slice(0, n - 1) + "…" : str;
}
